import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/Table';
import { TriangleBoth } from '../ui/SortIcons';
import { fetchProviderClaims } from '../../services/referralService';
import { LoadingSpinner } from '../LoadingSpinner';

// Display order for the Provider Claims tab. Two columns only — the legacy
// provider-claims report shows clm_id + max_paid_dt and nothing else.
const COLUMNS = [
  { key: 'claim_id', label: 'Claim ID' },
  { key: 'paid_date', label: 'Paid Date' },
];

// Ten asterisks matches the legacy myDBR convention used by
// sp_DBR_claims_administrator_dashboard_provider_claim_download and the parent
// sp_DBR_claims_admin_referral_list. Hard-coded — do not parameterize.
const PHI_MASK = '**********';
const EMPTY = '—';

// Cap on displayed rows. Mirrors PROVIDER_CLAIMS_DISPLAY_CAP in
// backend/carrier_portal/services/provider_claims_service.py — the backend
// returns at most this many rows; this constant exists so the footer can
// compute the truncated-vs-not branch without a magic number.
const DISPLAY_CAP = 100;

function renderCell(value, isPhiMasked) {
  // PHI-masked wins over the null branch. The backend sends null AND adds
  // the field to phi_masked_fields when the user lacks PHI access; em-dash
  // remains reserved for genuinely empty data.
  if (isPhiMasked) return PHI_MASK;
  if (value === null || value === undefined || value === '') {
    return EMPTY;
  }
  return String(value);
}

function recordCountLabelFor(displayedCount, totalCount) {
  if (totalCount === 0) return '0 records';
  if (totalCount > displayedCount) {
    return `Showing top ${displayedCount} of ${totalCount}`;
  }
  return totalCount === 1 ? '1 record' : `${totalCount} records`;
}

function ProviderClaimsTab({ refId }) {
  const [rows, setRows] = useState([]);
  // total is the unfiltered DB row count from the backend's `count` field —
  // not the rendered row count. rows.length can be smaller when the backend
  // capped at DISPLAY_CAP.
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!refId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);
    setTotal(0);

    fetchProviderClaims(refId)
      .then((response) => {
        if (!cancelled) {
          setRows(Array.isArray(response?.results) ? response.results : []);
          setTotal(typeof response?.count === 'number' ? response.count : 0);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refId]);

  const recordCountLabel = recordCountLabelFor(rows.length, total);

  return (
    // Outer padding gives the table card breathing room from the drawer edges,
    // matching the visual treatment in the design mockup.
    <div className="px-6 py-4 flex flex-col min-h-0 flex-1">
      <div className="flex flex-col min-h-0 flex-1 border border-border rounded-md overflow-hidden bg-background">
        {/*
          Single overflow-auto container so the sticky <thead> resolves its
          scroll context against this element (nested vertical + horizontal
          scroll containers break sticky positioning).
        */}
        <div className="flex-1 min-h-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                {COLUMNS.map((col) => (
                  <TableHead
                    key={col.key}
                    className="h-8 px-3 font-semibold text-xs whitespace-nowrap text-foreground align-middle"
                  >
                    <div className="flex items-center gap-1.5">
                      {col.label}
                      {/*
                        Decorative sort icon only — no click handler, no state.
                        Matches the referral table's visual style for now;
                        functional sorting will be wired up in a later ticket.
                      */}
                      <TriangleBoth className="h-3 w-3 opacity-[0.35]" />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    <LoadingSpinner label="Loading provider claims" className="mx-auto" />
                  </TableCell>
                </TableRow>
              )}

              {!loading && error && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    className="py-8 text-center text-sm text-destructive"
                    data-testid="provider-claims-error"
                  >
                    Unable to load provider claims.
                  </TableCell>
                </TableRow>
              )}

              {!loading && !error && rows.map((row, idx) => (
                <TableRow
                  key={`${row.claim_id ?? 'row'}-${row.paid_date ?? ''}-${idx}`}
                >
                  {COLUMNS.map((col) => {
                    // PhiMaskingMixin omits phi_masked_fields entirely when
                    // the user has PHI access — the Array.isArray guard keeps
                    // this safe against undefined access on those rows.
                    const isMasked =
                      Array.isArray(row.phi_masked_fields) &&
                      row.phi_masked_fields.includes(col.key);
                    return (
                      <TableCell
                        key={col.key}
                        className="px-3 py-1 text-xs text-foreground align-middle whitespace-nowrap text-left"
                      >
                        {renderCell(row[col.key], isMasked)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/*
          Footer anchors to the bottom of the flex column. When the backend
          caps results at DISPLAY_CAP, the label flips to "Showing top N of
          {total}" — the only signal users have that there's more data than
          shown. No Download button on this tab (no export endpoint exists
          and the parent legacy report is an Excel-only path).
        */}
        <div className="flex items-center justify-center gap-6 border-t border-border bg-muted/30 px-4 py-2 text-xs">
          <span className="text-muted-foreground">{recordCountLabel}</span>
        </div>
      </div>
    </div>
  );
}

export { ProviderClaimsTab, DISPLAY_CAP };
